'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import PubSub from '@src/quince-site-common/src/redux/PubSub'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { generateRandomString } from '@src/quince-site-common/src/utils/stringUtil'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { getCurrentFindingMethod } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { buildPageViewTrackingContext } from './useOnRouteUpdateWithStateFlush.helpers'

/**
 * Enhanced version of useOnRouteUpdate that also flushes specified state managers on navigation
 * @param stateManagers - Optional array of state managers to reset on navigation
 * @param pageProps - Page props from Next.js containing page data and tracking context
 */
export function useOnRouteUpdateWithStateFlush(stateManagers?: PubSub[], pageProps?: ServerSideBaseProps) {
  const prevPathnameRef = useRef<string | undefined>(undefined)
  const isInitialMountRef = useRef(true)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const trackingContext = pageProps?.trackingContext || {}

  // Set __client_side_navigation__ flag SYNCHRONOUSLY during render.
  // This ensures the flag is set BEFORE any child useEffects fire (e.g., PRODUCT_VIEW tracking).
  // React executes child effects before parent effects, so setting this in a useEffect
  // would be too late for child tracking events.
  if (isBrowser()) {
    if (isInitialMountRef.current) {
      // First mount - don't set the flag, this is not a client-side navigation
      isInitialMountRef.current = false
    } else if (prevPathnameRef.current && pathname !== prevPathnameRef.current) {
      // Subsequent navigations - set the flag immediately during render
      window.__client_side_navigation__ = true
    }
  }

  useEffect(() => {
    const prevPathname = prevPathnameRef.current
    if (!prevPathname || pathname !== prevPathname) {
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.PAGE_VIEW, {
        ...trackingContext,
        context: buildPageViewTrackingContext(currentFindingMethod),
      })
    }
    if (prevPathname && pathname !== prevPathname) {
      // Flush state managers on navigation
      if (stateManagers && stateManagers.length > 0) {
        stateManagers.forEach(manager => {
          if (manager.resetState) {
            manager.resetState()
          }
        })
      }
    }
    window.__unique_path_hash__ = generateRandomString()

    // Update ref for next render's comparison
    prevPathnameRef.current = pathname
  }, [pathname, searchParams, stateManagers, trackingContext])
}
